import { supabase } from '../lib/supabase';

export interface Brand {
  id: string;
  name: string;
  aliases?: string[] | null;
  created_at: string;
  created_by_user_id?: string | null;
}

export interface ProductModel {
  id: string;
  product_family_id: string;
  brand_uuid_id?: string | null;
  name: string;
  created_at: string;
}

export interface ProductFamily {
  id: string;
  brand_id: string;
  name: string;
  category_id?: string | null;
  created_at: string;
  created_by_user_id?: string;
}

/**
 * Fetch all brands from the database
 */
export async function getAllBrands(): Promise<Brand[]> {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching brands:', error);
    throw error;
  }

  // Cast aliases from JSONB to string array
  return (data || []).map(brand => ({
    ...brand,
    aliases: brand.aliases as string[] | null,
  }));
}

/**
 * Fetch all families for a specific brand
 */
export async function getFamiliesByBrand(brandId: string): Promise<ProductFamily[]> {
  const { data, error } = await supabase
    .from('product_families')
    .select('*')
    .eq('brand_id', brandId)
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching families:', error);
    throw error;
  }

  return data || [];
}

/**
 * Fetch all models for a specific family
 */
export async function getModelsByFamily(familyId: string): Promise<ProductModel[]> {
  const { data, error } = await supabase
    .from('product_models')
    .select('*')
    .eq('product_family_id', familyId)
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching models:', error);
    throw error;
  }

  return data || [];
}

/**
 * @deprecated Use getModelsByFamily instead
 * Fetch all models for a specific brand (legacy function)
 */
export async function getModelsByBrand(brandId: string): Promise<ProductModel[]> {
  const { data, error } = await supabase
    .from('product_models')
    .select(`
      id,
      name,
      product_family_id,
      created_at,
      product_families!inner(brand_id)
    `)
    .eq('product_families.brand_id', brandId)
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching models:', error);
    throw error;
  }

  return data || [];
}

/**
 * Extract family name from model name
 * Examples:
 * - "iPhone 13 Pro" → "iPhone"
 * - "MacBook Air M2" → "MacBook"
 * - "Galaxy S21 Ultra" → "Galaxy"
 * - "iPad Pro 12.9" → "iPad"
 */
export function extractFamilyFromModelName(modelName: string): string {
  // Common suffixes to remove
  const suffixes = ['Pro', 'Max', 'Plus', 'Mini', 'Air', 'SE', 'Ultra', 'Lite', 'Prime'];
  
  let family = modelName.trim();
  
  // Remove trailing numbers and suffixes
  // First remove numbers like "13", "14", "2022", etc.
  family = family.replace(/\s+\d+(\.\d+)?.*$/, '');
  
  // Then remove common suffixes
  suffixes.forEach(suffix => {
    const regex = new RegExp(`\\s+${suffix}$`, 'i');
    family = family.replace(regex, '');
  });
  
  // If we removed everything, return the first word
  if (!family.trim()) {
    family = modelName.split(/\s+/)[0];
  }
  
  return family.trim();
}

/**
 * Create a new brand
 * @param name - Brand name
 * @param userId - Optional user ID (will be set automatically by RLS if not provided)
 */
export async function createBrand(name: string, userId?: string): Promise<Brand> {
  const insertData: any = { name };
  if (userId) {
    insertData.created_by_user_id = userId;
  }
  
  const { data, error } = await supabase
    .from('brands')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error creating brand:', error);
    throw error;
  }

  return {
    ...data,
    aliases: data.aliases as string[] | null,
  };
}

/**
 * Create a new product family
 * @param brandId - Brand ID
 * @param name - Family name
 * @param userId - Optional user ID (will be set automatically by RLS if not provided)
 * @param categoryId - Optional category ID for marketplace taxonomy
 */
export async function createProductFamily(
  brandId: string, 
  name: string, 
  userId?: string,
  categoryId?: string | null
): Promise<ProductFamily> {
  const insertData: any = { brand_id: brandId, name };
  if (userId) {
    insertData.created_by_user_id = userId;
  }
  if (categoryId) {
    insertData.category_id = categoryId;
  }
  
  const { data, error } = await supabase
    .from('product_families')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error creating product family:', error);
    throw error;
  }

  return data;
}

/**
 * Create a new product model
 * @param familyId - Family ID
 * @param name - Model name
 * @param brandId - Brand UUID. Denormalised on the model so search/filter
 *                 queries don't need to traverse the family every time, and
 *                 so the model has a single source of truth for its brand.
 * @param userId - Optional user ID (will be set automatically by RLS if not provided)
 */
export async function createProductModel(
  familyId: string,
  name: string,
  brandId: string,
  userId?: string,
): Promise<ProductModel> {
  const insertData: any = { product_family_id: familyId, name, brand_uuid_id: brandId };
  if (userId) {
    insertData.created_by_user_id = userId;
  }

  const { data, error } = await supabase
    .from('product_models')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error creating product model:', error);
    throw error;
  }

  return data;
}

/**
 * Create a new product variant
 */
export async function createProductVariant(modelId: string, attributes: any = {}): Promise<any> {
  const { data, error } = await supabase
    .from('product_variants')
    .insert({ model_id: modelId, attributes })
    .select()
    .single();

  if (error) {
    console.error('Error creating product variant:', error);
    throw error;
  }

  return data;
}

/**
 * Find or create a complete product hierarchy (brand -> family -> model -> variant)
 * This is useful when updating a user product with new brand/model information
 * 
 * @param brandName - The brand name (e.g., "Apple")
 * @param modelName - The model name (e.g., "13 Pro")
 * @param familyName - Optional family name. If not provided, will be extracted from modelName
 * @param attributes - Variant attributes (e.g., {color: "Gold", storage: "256GB"})
 * @returns Object containing IDs for variant, brand, family, and model
 */
export async function findOrCreateProductHierarchy(
  brandName: string,
  modelName: string,
  familyName?: string,
  attributes: any = {}
): Promise<{ variantId: string; brandId: string; familyId: string; modelId: string }> {
  // 1. Find or create brand
  // First try to find with case-insensitive search (without .single())
  let brandResult = await supabase
    .from('brands')
    .select('*')
    .ilike('name', brandName)
    .limit(1);

  let brandData: Brand;
  if (brandResult.error || !brandResult.data || brandResult.data.length === 0) {
    brandData = await createBrand(brandName);
  } else {
    brandData = {
      ...brandResult.data[0],
      aliases: brandResult.data[0].aliases as string[] | null,
    };
  }

  // 2. Find or create family
  // If family name not provided, try to extract it from model name
  // Otherwise fall back to brand name
  const familyNameToUse = familyName || extractFamilyFromModelName(modelName) || brandName;
  let familyResult = await supabase
    .from('product_families')
    .select('*')
    .eq('brand_id', brandData.id)
    .ilike('name', familyNameToUse)
    .limit(1);

  let familyData: ProductFamily;
  if (familyResult.error || !familyResult.data || familyResult.data.length === 0) {
    familyData = await createProductFamily(brandData.id, familyNameToUse);
  } else {
    familyData = familyResult.data[0];
  }

  // 3. Find or create model
  //    Filter lookup by brand too — same shape as the insert below — so an
  //    existing branded model is reused instead of being shadowed by a new
  //    brand-less duplicate.
  let modelResult = await supabase
    .from('product_models')
    .select('*')
    .eq('product_family_id', familyData.id)
    .eq('brand_uuid_id', brandData.id)
    .ilike('name', modelName)
    .limit(1);

  let modelData: ProductModel;
  if (modelResult.error || !modelResult.data || modelResult.data.length === 0) {
    modelData = await createProductModel(familyData.id, modelName, brandData.id);
  } else {
    modelData = modelResult.data[0];
  }

  // 4. Find or create variant
  let variantResult = await supabase
    .from('product_variants')
    .select('*')
    .eq('model_id', modelData.id)
    .single();

  let variantData: any;
  if (variantResult.error || !variantResult.data) {
    variantData = await createProductVariant(modelData.id, attributes);
  } else {
    variantData = variantResult.data;
  }

  return {
    variantId: variantData.id,
    brandId: brandData.id,
    familyId: familyData.id,
    modelId: modelData.id,
  };
}
